import React, { useEffect, useState, useRef } from 'react';
import { userApi } from '../api';
import AnimeCard from './AnimeCard';
import AnimeDetailsDrawer from './AnimeDetailsDrawer';
import { Upload, Loader2, Search, Library, Frown, ChevronDown, SlidersHorizontal, ArrowDownUp, RefreshCw, Download, ArrowUp, ArrowDown, CheckCircle2, XCircle } from 'lucide-react';

const Dashboard = () => {
  const [animeList, setAnimeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatuses, setFilterStatuses] = useState(['watching', 'completed']);
  const [sortBy, setSortBy] = useState('recent'); // status, alphabetical, recent, created
  const [sortDirection, setSortDirection] = useState('asc'); // asc, desc
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [visibleCount, setVisibleCount] = useState(48);
  const [selectedAnime, setSelectedAnime] = useState(null);

  const [user, setUser] = useState(null);

  const sortMenuRef = useRef(null);
  const actionsMenuRef = useRef(null);

  // Écouteur de scroll pour l'Infinite Scroll Frontend
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 500) {
        setVisibleCount(prev => prev + 48);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset de la pagination quand la recherche change
  useEffect(() => {
    setVisibleCount(48);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setShowSortMenu(false);
      }
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const [animeRes, userRes] = await Promise.all([
        userApi.getUserAnimes(),
        userApi.getMe()
      ]);
      setAnimeList(animeRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error("Fetch failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Polling pour l'import MAL
  useEffect(() => {
    let interval;
    if (importProgress?.is_running && importProgress?.source === 'mal') {
      interval = setInterval(async () => {
        try {
          const res = await userApi.getImportStatus();
          if (res.data) {
            setImportProgress(prev => ({ ...res.data, source: 'mal', title: prev?.title }));
            if (!res.data.is_running) {
              fetchData(); // Recharger quand l'import background MAL est terminé
            }
          }
        } catch (e) { }
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [importProgress?.is_running, importProgress?.source]);

  const handleCancelImport = async () => {
    if (importProgress?.source === 'local') {
      window._cancelLocalImport = true;
    } else {
      try {
        await userApi.cancelImport();
        setImportProgress(prev => prev ? { ...prev, is_running: false, current_anime: 'Annulé' } : null);
      } catch (e) { }
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setImporting(true);
        const data = JSON.parse(e.target.result);
        const urls = Object.keys(data);

        let currentHistory = [];
        setImportProgress({ title: "Importation Locale", is_running: true, source: 'local', total: urls.length, processed: 0, history: currentHistory, current_anime: "Démarrage..." });

        window._cancelLocalImport = false;

        for (let i = 0; i < urls.length; i++) {
          if (window._cancelLocalImport) {
            currentHistory = [{ title: "Action", status: 'error', error: "Annulé par l'utilisateur" }, ...currentHistory];
            setImportProgress(prev => prev ? { ...prev, history: currentHistory } : null);
            break;
          }

          const url = urls[i];
          setImportProgress(prev => prev ? { ...prev, current_anime: url, processed: i } : null);

          try {
            await userApi.importHistory({ [url]: data[url] });
            currentHistory = [{ title: url, status: 'success' }, ...currentHistory];
          } catch (err) {
            currentHistory = [{ title: url, status: 'error', error: "Échec" }, ...currentHistory];
          }
          setImportProgress(prev => prev ? { ...prev, history: currentHistory } : null);
        }

        setImportProgress(prev => prev ? { ...prev, is_running: false, current_anime: window._cancelLocalImport ? "Annulé" : "Terminé", processed: window._cancelLocalImport ? prev.processed : urls.length } : null);
        fetchData();
      } finally {
        setImporting(false);
        event.target.value = null; // Reset l'input file
      }
    };
    reader.readAsText(file);
  };

  const handleSyncMal = async () => {
    try {
      setImporting(true);
      await userApi.syncMalHistory();
      setImportProgress({ title: "Exportation vers MAL", is_running: true, source: 'mal', total: 0, processed: 0, history: [], current_anime: "Initialisation Sync..." });
    } catch (e) {
      alert("Erreur lors de la synchro MAL.");
    } finally {
      setImporting(false);
    }
  };

  const handleImportMal = async () => {
    try {
      setImporting(true);
      await userApi.importMalHistory();
      setImportProgress({ title: "Importation depuis MAL", is_running: true, source: 'mal', total: 0, processed: 0, history: [], current_anime: "Initialisation MAL..." });
    } catch (e) {
      alert("Erreur lors du lancement de l'import MAL.");
    } finally {
      setImporting(false);
    }
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      // Sens par défaut pour chaque tri
      if (newSortBy === 'recent') setSortDirection('desc'); // Plus récents d'abord
      else if (newSortBy === 'created') setSortDirection('desc'); // Plus récents ajouts d'abord
      else if (newSortBy === 'alphabetical') setSortDirection('asc'); // A -> Z
      else setSortDirection('asc'); // Statut ascendant
    }
    setShowSortMenu(false);
    setVisibleCount(48); // Reset la pagination au changement de tri
  };

  const toggleFilter = (status) => {
    setFilterStatuses(prev => {
      const isCurrentlyActive = prev.includes(status);
      const exclusiveStatuses = ['plan_to_watch', 'on_hold', 'dropped'];

      // Si on clique sur un statut pour le désactiver
      if (isCurrentlyActive) {
        return prev.filter(s => s !== status);
      }

      // Si on active un statut exclusif, on désactive TOUT le reste
      if (exclusiveStatuses.includes(status)) {
        return [status];
      }

      // Si on active un statut NON-exclusif (watching, completed)
      // On l'ajoute, mais on supprime d'abord tous les statuts exclusifs potentiellement actifs
      const newFilters = prev.filter(s => !exclusiveStatuses.includes(s));
      return [...newFilters, status];
    });
    setVisibleCount(48); // Reset la pagination au changement de filtre
  };

  const filteredAnimes = animeList
    .filter(anime => {
      const searchLower = searchTerm.toLowerCase();
      const title = anime.romaji || anime.english || anime.french || anime.voiranime_slug || "";
      if (searchTerm && !title.toLowerCase().includes(searchLower)) return false;

      // On filtre sur le statut si des filtres sont actifs
      if (filterStatuses.length > 0 && !filterStatuses.includes(anime.mal_status)) return false;

      return true;
    })
    .sort((a, b) => {
      let result = 0;

      if (sortBy === 'alphabetical') {
        const titleA = a.french || a.english || a.romaji || a.voiranime_slug || "";
        const titleB = b.french || b.english || b.romaji || b.voiranime_slug || "";
        result = titleA.localeCompare(titleB);
      } else if (sortBy === 'recent') {
        const getValidTime = (dateStr) => {
          if (!dateStr) return 0;
          const time = new Date(dateStr).getTime();
          return isNaN(time) ? 0 : time;
        };

        // On prend la date la plus récente parmi toutes les dates de visionnage disponibles
        const dateA = Math.max(
          getValidTime(a.last_watched_date),
          getValidTime(a.end_watch_date),
          getValidTime(a.mal_start_date),
          getValidTime(a.mal_finish_date)
        );
        const dateB = Math.max(
          getValidTime(b.last_watched_date),
          getValidTime(b.end_watch_date),
          getValidTime(b.mal_start_date),
          getValidTime(b.mal_finish_date)
        );

        if (dateA === dateB) {
          const titleA = a.french || a.english || a.romaji || a.voiranime_slug || "";
          const titleB = b.french || b.english || b.romaji || b.voiranime_slug || "";
          result = titleA.localeCompare(titleB);
        } else {
          result = dateA - dateB;
        }
      } else if (sortBy === 'created') {
        const getValidTime = (dateStr) => {
          if (!dateStr) return 0;
          const time = new Date(dateStr).getTime();
          return isNaN(time) ? 0 : time;
        };
        const dateA = getValidTime(a.created_at);
        const dateB = getValidTime(b.created_at);
        result = dateA - dateB;
      } else {
        const order = { 'watching': 1, 'plan_to_watch': 2, 'on_hold': 3, 'completed': 4, 'dropped': 5 };
        const rankA = order[a.mal_status] || 99;
        const rankB = order[b.mal_status] || 99;
        result = rankA - rankB;
      }

      return sortDirection === 'asc' ? result : -result;
    });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-blue-500 relative overflow-hidden">
        {/* Fond de chargement cohérent */}
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[120px] animate-pulse"></div>
        <Loader2 size={48} className="animate-spin mb-4 relative z-10" />
        <p className="text-gray-400 animate-pulse relative z-10">Chargement de votre bibliothèque...</p>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-900 text-white pb-20 relative selection:bg-blue-500 selection:text-white">

        {/* --- NOUVEAU : ARRIÈRE-PLAN DÉGRADÉ FIXE --- */}
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          {/* Lueure Bleue (Haut Gauche) */}
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[120px] opacity-70"></div>

          {/* Lueure Violette (Bas Droite) */}
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/10 rounded-full blur-[120px] opacity-70"></div>

          {/* Lueure Centrale (Subtile) */}
          <div className="absolute top-[40%] left-[50%] translate-x-[-50%] w-[60%] h-[60%] bg-indigo-900/10 rounded-full blur-[150px]"></div>
        </div>

        {/* --- CONTENU PRINCIPAL (Z-INDEX 10 pour passer au dessus du fond) --- */}
        <div className="relative z-10">

          {/* --- HEADER & CONTROLS --- */}
          <div className="sticky top-[60px] z-40 bg-gray-900/80 backdrop-blur-xl border-b border-gray-800/50 shadow-sm transition-all">
            <div className="w-full lg:w-[85%] xl:w-[75%] mx-auto px-4 py-6">

              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

                {/* Titre et Stats */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Library className="text-blue-500" size={24} />
                    <h1 className="text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
                      Ma Bibliothèque
                    </h1>
                  </div>
                  <p className="text-sm text-gray-500">
                    {animeList.length} anime{animeList.length > 1 ? 's' : ''} suivi{animeList.length > 1 ? 's' : ''}
                  </p>
                </div>

                {/* Filtres de statut */}
                <div className="flex gap-2 items-center flex-wrap">
                  {['watching', 'completed', 'plan_to_watch', 'on_hold', 'dropped'].map(status => {
                    const isActive = filterStatuses.includes(status);
                    const labels = {
                      watching: "En cours",
                      completed: "Complétés",
                      plan_to_watch: "Watchlist",
                      on_hold: "En pause",
                      dropped: "Abandonnés"
                    };

                    const activeStyles = {
                      watching: 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-500/20',
                      completed: 'bg-green-600 text-white border-green-500 shadow-md shadow-green-500/20',
                      plan_to_watch: 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-500/20',
                      on_hold: 'bg-orange-600 text-white border-orange-500 shadow-md shadow-orange-500/20',
                      dropped: 'bg-red-600 text-white border-red-500 shadow-md shadow-red-500/20',
                    };

                    return (
                      <button
                        key={status}
                        onClick={() => toggleFilter(status)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${isActive
                          ? activeStyles[status]
                          : 'bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700 hover:text-gray-200'
                          }`}
                      >
                        {labels[status]}
                      </button>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="flex w-full md:w-auto gap-3 flex-wrap md:flex-nowrap">

                  {/* Barre de recherche */}
                  <div className="relative flex-grow md:flex-grow-0 group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-500 transition-colors">
                      <Search size={18} />
                    </div>
                    <input
                      type="text"
                      placeholder="Rechercher un anime..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full md:w-64 pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all placeholder-gray-500 hover:bg-gray-800"
                    />
                  </div>

                  <div className="flex gap-2 w-full md:w-auto">

                    {/* Menu de Tri */}
                    <div className="relative" ref={sortMenuRef}>
                      <button
                        onClick={() => { setShowSortMenu(!showSortMenu); setShowActionsMenu(false); }}
                        className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-all border border-gray-700 text-sm font-semibold h-full"
                      >
                        <ArrowDownUp size={16} />
                        <span className="hidden sm:inline">Trier</span>
                        <ChevronDown size={14} className={`transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
                      </button>

                      {showSortMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden z-50">
                          <button onClick={() => handleSortChange('status')} className={`w-full flex items-center justify-between text-left px-4 py-2.5 text-sm hover:bg-gray-700 transition-colors ${sortBy === 'status' ? 'text-blue-400 font-semibold bg-gray-700/50' : 'text-gray-300'}`}>
                            <span>Par Statut</span>
                            {sortBy === 'status' && (sortDirection === 'asc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />)}
                          </button>
                          <button onClick={() => handleSortChange('recent')} className={`w-full flex items-center justify-between text-left px-4 py-2.5 text-sm hover:bg-gray-700 transition-colors ${sortBy === 'recent' ? 'text-blue-400 font-semibold bg-gray-700/50' : 'text-gray-300'}`}>
                            <span>Date de visionnage</span>
                            {sortBy === 'recent' && (sortDirection === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />)}
                          </button>
                          <button onClick={() => handleSortChange('created')} className={`w-full flex items-center justify-between text-left px-4 py-2.5 text-sm hover:bg-gray-700 transition-colors ${sortBy === 'created' ? 'text-blue-400 font-semibold bg-gray-700/50' : 'text-gray-300'}`}>
                            <span>Date d'ajout</span>
                            {sortBy === 'created' && (sortDirection === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />)}
                          </button>
                          <button onClick={() => handleSortChange('alphabetical')} className={`w-full flex items-center justify-between text-left px-4 py-2.5 text-sm hover:bg-gray-700 transition-colors ${sortBy === 'alphabetical' ? 'text-blue-400 font-semibold bg-gray-700/50' : 'text-gray-300'}`}>
                            <span>Alphabétique</span>
                            {sortBy === 'alphabetical' && (sortDirection === 'asc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />)}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Menu d'Actions */}
                    <div className="relative" ref={actionsMenuRef}>
                      <button
                        onClick={() => { setShowActionsMenu(!showActionsMenu); setShowSortMenu(false); }}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all border border-blue-500 shadow-md shadow-blue-500/20 text-sm font-semibold h-full"
                      >
                        {importing ? <Loader2 size={16} className="animate-spin" /> : <SlidersHorizontal size={16} />}
                        <span className="hidden sm:inline">Actions</span>
                        <ChevronDown size={14} className={`transition-transform ${showActionsMenu ? 'rotate-180' : ''}`} />
                      </button>

                      {showActionsMenu && (
                        <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 flex flex-col p-1">
                          {user?.mal_connected && (
                            <>
                              <button onClick={() => { setShowActionsMenu(false); handleImportMal(); }} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-200 hover:bg-indigo-600 hover:text-white rounded-lg transition-colors text-left">
                                <Download size={16} /> Importer depuis MAL
                              </button>
                              <button onClick={() => { setShowActionsMenu(false); handleSyncMal(); }} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-200 hover:bg-blue-600 hover:text-white rounded-lg transition-colors text-left">
                                <RefreshCw size={16} /> Exporter vers MAL
                              </button>
                              <div className="h-px bg-gray-700 my-1 mx-2"></div>
                            </>
                          )}
                          <label className="flex items-center gap-3 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 rounded-lg cursor-pointer transition-colors m-0">
                            <Upload size={16} /> Import local (.json)
                            <input type="file" accept=".json" onChange={(e) => { setShowActionsMenu(false); handleImport(e); }} className="hidden" />
                          </label>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- GRILLE D'ANIMES --- */}
          <div className="w-full lg:w-[85%] xl:w-[75%] mx-auto px-4 mt-8 pb-12">

            {filteredAnimes.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6">
                {filteredAnimes.slice(0, visibleCount).map(anime => (
                  <AnimeCard key={anime.id} anime={anime} onClick={() => setSelectedAnime(anime)} />
                ))}
              </div>
            ) : (
              /* --- EMPTY STATE --- */
              <div className="flex flex-col items-center justify-center py-20 text-center opacity-0 animate-fadeIn" style={{ animationFillMode: 'forwards', animationDuration: '0.5s' }}>
                <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-full mb-4 border border-gray-700/50 shadow-xl">
                  {searchTerm ? <Search size={48} className="text-gray-500" /> : <Frown size={48} className="text-gray-500" />}
                </div>
                <h3 className="text-xl font-bold text-gray-300 mb-2">
                  {searchTerm ? "Aucun résultat trouvé" : "C'est bien vide ici..."}
                </h3>
                <p className="text-gray-500 max-w-md">
                  {searchTerm
                    ? `Nous n'avons trouvé aucun anime correspondant à "${searchTerm}".`
                    : "Commencez par regarder des épisodes ou importez votre historique pour remplir votre bibliothèque !"}
                </p>
                {!searchTerm && (
                  <label className="mt-6 text-blue-400 hover:text-blue-300 cursor-pointer text-sm font-semibold flex items-center gap-2 hover:underline">
                    <Upload size={16} />
                    Importer un fichier JSON maintenant
                    <input type="file" accept=".json" onChange={handleImport} className="hidden" />
                  </label>
                )}
              </div>
            )}

          </div>
        </div>

        {/* --- VOLET DE PROGRESSION IMPORT --- */}
        {importProgress && (
          <div className="fixed bottom-4 right-4 w-80 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-96">
            <div className="bg-gray-900 px-4 py-3 border-b border-gray-700 flex justify-between items-center">
              <h3 className="font-bold text-sm text-white">{importProgress.title || "Progression"}</h3>
              {importProgress.is_running ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-blue-400 font-semibold animate-pulse">{importProgress.processed} / {importProgress.total}</span>
                  <button onClick={handleCancelImport} className="text-xs font-semibold text-red-400 hover:text-red-300 bg-red-400/10 hover:bg-red-400/20 px-2 py-1 rounded transition-colors">Annuler</button>
                </div>
              ) : (
                <button onClick={() => setImportProgress(null)} className="text-gray-400 hover:text-white transition-colors">Fermer</button>
              )}
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              {importProgress.is_running && (
                <div className="mb-4 bg-gray-900/50 p-3 rounded-lg border border-gray-700/50">
                  <p className="text-xs text-gray-400 mb-2">En cours de traitement :</p>
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-blue-500 flex-shrink-0" />
                    <span className="text-sm font-semibold truncate text-white" title={importProgress.current_anime}>
                      {importProgress.current_anime || 'Initialisation...'}
                    </span>
                  </div>
                </div>
              )}

              {!importProgress.is_running && (!importProgress.history || importProgress.history.length === 0) ? (
                <div className="text-center py-6 text-gray-400 text-sm italic">
                  Tout est déjà parfaitement synchronisé !
                </div>
              ) : (
                <div className="space-y-2.5">
                  {importProgress.history?.map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm bg-gray-900/30 p-2 rounded border border-gray-800">
                      {item.status === 'success' ? <CheckCircle2 size={16} className="text-green-500 flex-shrink-0 mt-0.5" /> : <XCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />}
                      <div className="flex flex-col overflow-hidden">
                        <span className={`truncate font-medium ${item.status === 'error' ? 'text-red-400' : 'text-gray-200'}`} title={item.title}>
                          {item.title}
                        </span>
                        {item.error && <span className="text-xs text-red-500/70 truncate">{item.error}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Drawer détails anime */}
      {selectedAnime && (
        <AnimeDetailsDrawer
          anime={selectedAnime}
          user={user}
          onClose={() => setSelectedAnime(null)}
          onAnimeDeleted={(deletedId) => {
            setAnimeList(prev => prev.filter(a => a.id !== deletedId));
            setSelectedAnime(null);
          }}
          onAnimeUpdated={(updatedAnimeId, changes) => {
            setAnimeList(prev => prev.map(a => a.id === updatedAnimeId ? { ...a, ...changes } : a));
          }}
        />
      )}
    </>
  );
};

export default Dashboard;